import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { humanizeSupabaseError } from '../../../lib/supabaseErrors';
import { IEP_GOAL_TEMPLATES, templatesForArea } from '../../../lib/iepGoalTemplates';
import {
  draftGoal,
  hasApiKey,
  setApiKey,
} from '../../../lib/goalAi';

// Same modal is reused for creating and editing a goal. Pass `existingGoal`
// to prefill the form and switch the primary button copy to "Save changes".
//
// The modal offers three ways to fill in the form so teachers aren't locked
// into either extreme:
//   1. Blank — write it from scratch.
//   2. Templates — pick a SMART starter from the curated library and edit.
//   3. Draft with AI — describe the goal in plain language, hit the button,
//      done. If the admin has set up shared AI drafting (recommended), the
//      teacher doesn't need any API key at all. If not, a small "Use my own
//      OpenAI key" panel is offered as a fallback.

// ---------------------------------------------------------------------------
// Add IEP Goal modal.
//
// Backs the "+ New Goal" button on the IEP Goals & Objectives page. Writes
// SMART-structured goals into public.behavior_goals (the schema in
// supabase/migrations/0002_pbis_data_model.sql):
//
//   goal_area     — high-level domain (academic, behavioral, communication…)
//   specific      — what specifically will change
//   measurable    — how it will be measured (frequency, duration, %)
//   baseline      — starting point (optional)
//   target        — target level of performance
//   mastery       — e.g. "3 consecutive weeks at 80%" (optional)
//   target_date   — SMART "time-bound" (optional)
//   progress      — 0..100 (optional starting point)
//   status        — not_started / in_progress / mastered / discontinued
//
// The four NOT NULL columns (student_id, goal_area, specific, measurable,
// target) are enforced here so we fail fast with a friendly error instead of
// posting to Supabase and getting a raw Postgres constraint violation.
// ---------------------------------------------------------------------------

const GOAL_AREA_OPTIONS = [
  { value: 'academic', label: 'Academic' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'social-emotional', label: 'Social / Emotional' },
  { value: 'communication', label: 'Communication' },
  { value: 'functional', label: 'Functional / Life skills' },
  { value: 'motor', label: 'Fine or gross motor' },
  { value: 'transition', label: 'Transition / Post-secondary' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'mastered', label: 'Mastered' },
  { value: 'discontinued', label: 'Discontinued' },
];

const emptyForm = {
  studentId: '',
  goalArea: '',
  specific: '',
  measurable: '',
  baseline: '',
  target: '',
  masteryCriteria: '',
  targetDate: '',
  progressPercent: '',
  // Optional numeric metadata used by the data-point log. When set, every
  // data point recorded on the goal detail view is compared against
  // targetValue to auto-compute progressPercent.
  targetValue: '',
  unit: '',
  status: 'in_progress',
};

const AddGoalModal = ({
  isOpen,
  onClose,
  onCreated,
  onUpdated,
  defaultStudentId,
  existingGoal = null,
}) => {
  const { students } = useStudents();
  const { addGoal, updateGoal, isLive } = useIEPGoals();
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const dialogRef = useRef(null);
  const isEdit = Boolean(existingGoal?.id);

  // Stash onClose in a ref for the "reset form on open" effect below —
  // same pattern as AddStudentModal / Sidebar. Parents pass onClose as
  // an inline arrow so its identity changes on every parent render, and
  // if we listed it in the effect deps every re-render (context refresh,
  // students list update, etc.) would wipe the goal-writing form the
  // teacher had already been typing into.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // "blank" | "templates" | "ai" — controls which helper panel is shown at
  // the top of the modal. Editing an existing goal always starts on blank
  // (the form is prefilled with the row).
  const [helperMode, setHelperMode] = useState('blank');

  // Collapsible "advanced" section. Novice teachers see 6 fields
  // (student, area, what will change, how you'll measure, starting point,
  // target); everything else — mastery criteria, numeric target, unit,
  // target date, progress %, status — lives behind a "Show more options"
  // toggle. When editing an existing goal that already has any of these
  // filled in, we auto-open the advanced section so the teacher can see
  // and edit them.
  const [showAdvanced, setShowAdvanced] = useState(false);

  // AI-assist state
  const [aiKeyOnFile, setAiKeyOnFile] = useState(false);
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiIsDrafting, setAiIsDrafting] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuccess, setAiSuccess] = useState(null);
  // Reveal the BYO-key panel only after (a) the teacher explicitly asks for it
  // via "Use my own OpenAI key instead" or (b) the shared drafter returned
  // "not set up on this project yet".
  const [showByoKey, setShowByoKey] = useState(false);
  // Set when the umbrella tried the shared function and got a "not set up
  // yet" back. Used to render a friendly hint above the BYO panel.
  const [sharedAiUnavailable, setSharedAiUnavailable] = useState(false);

  const studentOptions = useMemo(() => {
    const list = Array.isArray(students) ? students : [];
    return list
      .filter((s) => s.status !== 'archived')
      .map((s) => ({
        value: s.id,
        label: `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed student',
      }));
  }, [students]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (existingGoal) {
      setFormData({
        studentId: existingGoal.studentId || '',
        goalArea: existingGoal.goalArea || '',
        specific: existingGoal.specific || '',
        measurable: existingGoal.measurable || '',
        baseline: existingGoal.baseline || '',
        target: existingGoal.target || '',
        masteryCriteria: existingGoal.masteryCriteria || '',
        targetDate: existingGoal.targetDate || '',
        progressPercent:
          existingGoal.progressPercent == null
            ? ''
            : String(existingGoal.progressPercent),
        targetValue:
          existingGoal.targetValue == null
            ? ''
            : String(existingGoal.targetValue),
        unit: existingGoal.unit || '',
        status: existingGoal.status || 'in_progress',
      });
    } else {
      setFormData({ ...emptyForm, studentId: defaultStudentId || '' });
    }
    setErrors({});
    setSubmitError(null);
    setHelperMode('blank');
    // Auto-expand the advanced section on edit if anything advanced is
    // already filled in — the teacher shouldn't have to hunt for the
    // "Show more options" toggle to see the values they already saved.
    if (existingGoal) {
      const hasAdvanced =
        !!existingGoal.masteryCriteria ||
        existingGoal.targetValue != null ||
        !!existingGoal.unit ||
        !!existingGoal.targetDate ||
        (existingGoal.progressPercent != null && existingGoal.progressPercent > 0) ||
        (existingGoal.status && existingGoal.status !== 'in_progress');
      setShowAdvanced(hasAdvanced);
    } else {
      setShowAdvanced(false);
    }
    setAiKeyOnFile(hasApiKey());
    setAiKeyInput('');
    setAiPrompt('');
    setAiError(null);
    setAiSuccess(null);
    setShowByoKey(false);
    setSharedAiUnavailable(false);

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const el = dialogRef.current?.querySelector('input, select, textarea');
      el?.focus?.();
    }, 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultStudentId, existingGoal]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Prefill the form from a curated SMART template. Student, target date,
  // progress and status are left as the teacher already set them — the
  // template only fills in the goal-writing fields.
  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setFormData((prev) => ({
      ...prev,
      goalArea: tpl.goalArea || prev.goalArea,
      specific: tpl.specific || '',
      measurable: tpl.measurable || '',
      baseline: tpl.baseline || '',
      target: tpl.target || '',
      masteryCriteria: tpl.masteryCriteria || '',
    }));
    setErrors({});
    setHelperMode('blank');
  };

  // Save (or clear) the OpenAI API key that lives in the teacher's browser.
  const handleSaveKey = () => {
    const trimmed = aiKeyInput.trim();
    setApiKey(trimmed);
    setAiKeyOnFile(Boolean(trimmed));
    setAiKeyInput('');
    setAiError(null);
    setAiSuccess(
      trimmed
        ? 'API key saved to this browser only. Never shared with anyone else.'
        : 'API key removed from this browser.',
    );
  };

  const handleClearKey = () => {
    setApiKey('');
    setAiKeyOnFile(false);
    setAiSuccess('API key removed from this browser.');
    setAiError(null);
  };

  const handleAiDraft = async ({ preferOwnKey = false } = {}) => {
    setAiError(null);
    setAiSuccess(null);
    if (!aiPrompt.trim()) {
      setAiError('Describe the goal you want in your own words first.');
      return;
    }
    setAiIsDrafting(true);
    try {
      const student = students?.find((s) => s.id === formData.studentId);
      const draft = await draftGoal(
        aiPrompt,
        { grade: student?.grade || '', goalArea: formData.goalArea },
        { preferOwnKey },
      );
      setFormData((prev) => ({
        ...prev,
        goalArea: draft.goalArea || prev.goalArea,
        specific: draft.specific || prev.specific,
        measurable: draft.measurable || prev.measurable,
        baseline: draft.baseline || prev.baseline,
        target: draft.target || prev.target,
        masteryCriteria: draft.masteryCriteria || prev.masteryCriteria,
      }));
      setErrors({});
      setAiSuccess('Draft applied — review every field before saving.');
      setHelperMode('blank');
    } catch (err) {
      // Always surface the ACTUAL error message from the server (or from
      // the client-side fetch layer). Hardcoded "not set up on this
      // district yet" messages hide the real problem — was it a missing
      // secret? A rejected API key? A rate limit? A CORS issue? — and
      // waste hours of debugging. When something's wrong with AI drafting,
      // the teacher and admin need to see what the server actually said.
      //
      // We do NOT auto-reveal the BYO-key panel any more. If the shared
      // drafter is down, teachers can still use "Write my own" and
      // "Start from template" without seeing a scary key-entry form.
      // The advanced BYO toggle stays available for power users who
      // click into it themselves.
      const serverMessage = err?.message || String(err);
      const isNotSetUp = Boolean(err?.__edgeUnavailable);
      if (isNotSetUp) setSharedAiUnavailable(true);
      setAiError(
        serverMessage +
          (isNotSetUp
            ? '\n\n(This message came directly from the AI drafter. If you\'re the admin, check Supabase → Edge Functions → draft-goal → Logs to see the full error, and re-read ADMIN_GUIDE.md § 5.5.)'
            : ''),
      );
    } finally {
      setAiIsDrafting(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.studentId) next.studentId = 'Choose a student';
    if (!formData.goalArea) next.goalArea = 'Choose a goal area';
    if (!formData.specific.trim())
      next.specific = 'Describe what specifically will change';
    if (!formData.measurable.trim())
      next.measurable = 'Describe how progress will be measured';
    if (!formData.target.trim()) next.target = 'Describe the target';
    if (formData.progressPercent !== '' && formData.progressPercent !== null) {
      const num = Number(formData.progressPercent);
      if (Number.isNaN(num) || num < 0 || num > 100) {
        next.progressPercent = 'Progress must be a number 0–100';
      }
    }
    if (formData.targetValue !== '' && formData.targetValue !== null) {
      const num = Number(formData.targetValue);
      if (Number.isNaN(num)) {
        next.targetValue = 'Numeric target must be a number';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    setSubmitError(null);
    try {
      const payload = {
        studentId: formData.studentId,
        goalArea: formData.goalArea,
        specific: formData.specific.trim(),
        measurable: formData.measurable.trim(),
        baseline: formData.baseline.trim() || null,
        target: formData.target.trim(),
        masteryCriteria: formData.masteryCriteria.trim() || null,
        targetDate: formData.targetDate || null,
        progressPercent:
          formData.progressPercent === '' || formData.progressPercent == null
            ? null
            : Number(formData.progressPercent),
        targetValue:
          formData.targetValue === '' || formData.targetValue == null
            ? null
            : Number(formData.targetValue),
        unit: formData.unit.trim() || null,
        status: formData.status || 'in_progress',
      };
      if (isEdit) {
        const saved = await updateGoal(existingGoal.id, payload);
        onUpdated?.(saved);
      } else {
        const created = await addGoal(payload);
        onCreated?.(created);
      }
      onClose?.();
    } catch (err) {
      setSubmitError(
        humanizeSupabaseError(
          err,
          'Could not save this goal. Please check your connection and try again.',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Single-scroll-container pattern — see AddStudentModal.jsx for the
  // full rationale. Nested overflow-y-auto containers cause an iOS
  // Safari bug where the outer scroll locks and the user can't reach
  // the top of a tall modal.
  const overlayDismiss = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/40 overflow-y-auto overscroll-contain"
      onClick={overlayDismiss}
    >
      <div
        className="min-h-full flex items-start sm:items-center justify-center p-4"
        onClick={overlayDismiss}
      >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-goal-title"
        className="w-full max-w-2xl rounded-xl bg-white shadow-xl my-4 sm:my-8"
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-5 sticky top-0 bg-white z-10 rounded-t-xl">
          <div>
            <h2
              id="add-goal-title"
              className="text-lg font-semibold text-gray-900"
            >
              {isEdit ? 'Edit IEP Goal' : 'New IEP Goal'}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {isLive
                ? 'Saved to your Supabase database.'
                : 'Saved locally in this browser (demo mode).'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Helper mode selector — hidden when editing an existing goal, since
            the form is already prefilled with the row we're editing. */}
        {!isEdit && (
          <div className="border-b border-gray-100 px-5 pt-3">
            <div className="flex gap-1 rounded-lg bg-gray-50 p-1 text-sm">
              <button
                type="button"
                onClick={() => setHelperMode('blank')}
                className={`flex-1 rounded-md px-3 py-1.5 font-medium transition-colors ${
                  helperMode === 'blank'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Write my own
              </button>
              <button
                type="button"
                onClick={() => setHelperMode('templates')}
                className={`flex-1 rounded-md px-3 py-1.5 font-medium transition-colors ${
                  helperMode === 'templates'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Start from template
              </button>
              <button
                type="button"
                onClick={() => setHelperMode('ai')}
                className={`flex-1 rounded-md px-3 py-1.5 font-medium transition-colors ${
                  helperMode === 'ai'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Draft with AI
              </button>
            </div>
            <p className="mt-2 mb-3 text-xs text-gray-500">
              {helperMode === 'blank' &&
                "Only two things are required: what skill will improve, and how you'll measure it. Everything else is optional."}
              {helperMode === 'templates' &&
                'Pick a starter that matches the area, then edit every field to match your student.'}
              {helperMode === 'ai' &&
                "Describe the goal in your own words, click the button, review what it suggests, and save. Nothing gets sent until you click Save."}
            </p>
          </div>
        )}

        {helperMode === 'templates' && !isEdit && (
          <div className="border-b border-gray-100 px-5 py-4 bg-gray-50">
            <div className="max-h-64 overflow-y-auto pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(formData.goalArea
                  ? templatesForArea(formData.goalArea)
                  : IEP_GOAL_TEMPLATES
                ).map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    className="text-left rounded-lg border border-gray-200 bg-white p-3 hover:border-primary hover:shadow-sm transition-all"
                  >
                    <div className="text-xs font-medium uppercase tracking-wide text-primary mb-1">
                      {tpl.goalArea}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mb-1">
                      {tpl.label}
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-2">
                      {tpl.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              After you pick one, every field opens for editing. Update the
              baseline and target to match your student before saving.
            </p>
          </div>
        )}

        {helperMode === 'ai' && !isEdit && (
          <div className="border-b border-gray-100 px-5 py-4 bg-gray-50 space-y-3">
            {aiError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-line">
                {aiError}
              </div>
            )}
            {aiSuccess && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                {aiSuccess}
              </div>
            )}

            <div>
              <label
                htmlFor="ai-prompt"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Describe the goal in your own words
              </label>
              <textarea
                id="ai-prompt"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Example: '3rd-grader who is behind in reading — needs help with main-idea comprehension using visual supports, weekly probes.'"
              />
              <p className="mt-1 text-xs text-gray-500">
                Don't include the student's name or other PII — the model
                doesn't need it. Grade level and baseline are helpful.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowByoKey((v) => !v);
                  setAiError(null);
                }}
                className="text-xs text-gray-500 underline hover:text-gray-700"
              >
                {showByoKey
                  ? 'Hide "use my own AI key" panel'
                  : 'Use my own AI key instead (advanced)'}
              </button>
              <Button
                type="button"
                variant="default"
                onClick={() => handleAiDraft({ preferOwnKey: showByoKey && aiKeyOnFile })}
                loading={aiIsDrafting}
                iconName={aiIsDrafting ? 'Loader2' : 'Sparkles'}
                iconPosition="left"
                disabled={
                  !aiPrompt.trim() ||
                  (showByoKey && !aiKeyOnFile)
                }
              >
                {aiIsDrafting ? 'Drafting…' : 'Draft with AI'}
              </Button>
            </div>

            {/* Bring-your-own-key panel — hidden by default. Auto-revealed
                when the shared drafter says "not set up on this project
                yet", so a single teacher can still try AI without waiting
                for their admin. */}
            {showByoKey && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900 mb-1">
                  Use your own AI API key
                </p>
                <p className="text-xs text-amber-900/80 mb-3">
                  {sharedAiUnavailable
                    ? "Shared AI drafting isn't set up on this district's Supabase yet. If you already have your own AI API key (Gemini or OpenAI), you can paste it here just for this browser. Otherwise, ask your admin to enable shared AI in ADMIN_GUIDE §5.5 — that turns this off for you and everyone else."
                    : 'Advanced: bypass the shared account and use your personal AI API key (Gemini or OpenAI) instead. Your key is stored only in this browser (localStorage) — never sent to Supabase, GitHub, or any other server besides the AI provider itself.'}
                </p>

                {!aiKeyOnFile ? (
                  <div className="flex gap-2">
                    <input
                      type="password"
                      autoComplete="off"
                      value={aiKeyInput}
                      onChange={(e) => setAiKeyInput(e.target.value)}
                      placeholder="AIzaSy… (Gemini) or sk-… (OpenAI)"
                      className="flex-1 rounded-md border border-input bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <Button
                      type="button"
                      variant="default"
                      onClick={handleSaveKey}
                      disabled={!aiKeyInput.trim()}
                    >
                      Save key
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-2 text-xs text-green-900">
                    <Icon name="ShieldCheck" size={14} />
                    <span className="flex-1">
                      Personal AI key on file for this browser.
                    </span>
                    <button
                      type="button"
                      onClick={handleClearKey}
                      className="underline hover:text-green-700"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <p className="mt-2 text-xs text-amber-900/70">
                  Get a Gemini key (recommended, free tier) at{' '}
                  <a
                    href="https://aistudio.google.com/apikey"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    aistudio.google.com/apikey
                  </a>
                  {' '}or an OpenAI key at{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    platform.openai.com/api-keys
                  </a>
                  .
                </p>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-line">
              {submitError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Student"
              placeholder={
                studentOptions.length === 0
                  ? 'Add a student first'
                  : 'Choose a student'
              }
              options={studentOptions}
              value={formData.studentId}
              onChange={(value) => handleChange('studentId', value)}
              error={errors.studentId}
              searchable
              required
              disabled={studentOptions.length === 0}
            />
            <Select
              label="Goal area"
              placeholder="Choose a domain"
              options={GOAL_AREA_OPTIONS}
              value={formData.goalArea}
              onChange={(value) => handleChange('goalArea', value)}
              error={errors.goalArea}
              required
            />
          </div>

          {/* Labels rewritten in plain English so a novice teacher doesn't
              have to know what "SMART goal" or "mastery criteria" means.
              The underlying data fields haven't changed — this is purely
              a copy pass on the labels + descriptions. */}
          <div>
            <label
              htmlFor="goal-specific"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              What skill will improve?{' '}
              <span className="text-red-500">*</span>
            </label>
            <p className="mb-1 text-xs text-gray-500">
              Describe the change you want to see, in a sentence or two.
            </p>
            <textarea
              id="goal-specific"
              value={formData.specific}
              onChange={(e) => handleChange('specific', e?.target?.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder='Example: "Joe will independently answer literal comprehension questions about a grade-level passage."'
            />
            {errors.specific && (
              <p className="mt-1 text-xs text-red-600">{errors.specific}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="goal-measurable"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              How will you know they're making progress?{' '}
              <span className="text-red-500">*</span>
            </label>
            <p className="mb-1 text-xs text-gray-500">
              What will you count, time, or observe? (Trials, percent
              correct, minutes on task…)
            </p>
            <textarea
              id="goal-measurable"
              value={formData.measurable}
              onChange={(e) => handleChange('measurable', e?.target?.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder='Example: "% of questions answered correctly across 3 trials per week."'
            />
            {errors.measurable && (
              <p className="mt-1 text-xs text-red-600">{errors.measurable}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Where are they starting? (optional)"
              value={formData.baseline}
              onChange={(e) => handleChange('baseline', e?.target?.value)}
              placeholder='e.g. "40% accuracy right now"'
              description="Their skill level today. Leave blank if you're not sure."
            />
            <Input
              label="Where do you want them to get to?"
              value={formData.target}
              onChange={(e) => handleChange('target', e?.target?.value)}
              error={errors.target}
              placeholder='e.g. "80% accuracy across 3 sessions in a row"'
              required
            />
          </div>

          {/* Advanced options — hidden by default. A first-time teacher
              adds 5 fields (student, area, skill, measure, target) and
              hits Save. Case managers who need the full SMART set can
              open this. */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Icon
                name={showAdvanced ? 'ChevronDown' : 'ChevronRight'}
                size={16}
              />
              {showAdvanced ? 'Hide' : 'Show'} more options (mastery, dates,
              status)
            </button>
            <p className="mt-1 text-xs text-gray-500">
              You don't need these to save the goal. Skip if you're just
              getting started.
            </p>
          </div>

          {showAdvanced && (
            <div className="space-y-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
              <div>
                <label
                  htmlFor="goal-mastery"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  When is the goal mastered? (optional)
                </label>
                <p className="mb-1 text-xs text-gray-500">
                  How many sessions or settings do you need to see the
                  target before you'd call it done?
                </p>
                <textarea
                  id="goal-mastery"
                  value={formData.masteryCriteria}
                  onChange={(e) => handleChange('masteryCriteria', e?.target?.value)}
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder='e.g. "3 weeks in a row at 80% accuracy in 2 different settings"'
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Target number (optional)"
                  type="number"
                  step="any"
                  value={formData.targetValue}
                  onChange={(e) => handleChange('targetValue', e?.target?.value)}
                  error={errors.targetValue}
                  placeholder='e.g. "80" for 80%'
                  description="Lets the app calculate progress % from your data points."
                />
                <Input
                  label="Unit (optional)"
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e?.target?.value)}
                  placeholder='e.g. "%", "words per minute", "minutes"'
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Target date (optional)"
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => handleChange('targetDate', e?.target?.value)}
                  description="When you'd like this goal to be met by."
                />
                <Input
                  label="Progress % (0–100)"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.progressPercent}
                  onChange={(e) =>
                    handleChange('progressPercent', e?.target?.value)
                  }
                  error={errors.progressPercent}
                  placeholder="0"
                  description="Updates automatically when you log data."
                />
                <Select
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={formData.status}
                  onChange={(value) => handleChange('status', value)}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSaving}
              iconName={isSaving ? 'Loader2' : 'Save'}
              iconPosition="left"
              disabled={studentOptions.length === 0}
            >
              {isSaving
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Save goal'}
            </Button>
          </div>

          {studentOptions.length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              You need at least one student on your roster before you can save
              an IEP goal. Add a student from the dashboard first.
            </p>
          )}
        </form>
      </div>
      </div>
    </div>
  );
};

export default AddGoalModal;
